import api from './api';
import type {
  MenuConsumptionRecipe,
  MenuConsumptionRecipeComponent,
  PaginatedResponse,
} from '../types/menu';

/**
 * INERT in Phase 2A/2B. These endpoints store consumption recipes but no
 * inventory, POS, or production pipeline reads them yet. UI surfaces must
 * clearly label this as storage-only — see the Phase 2B plan, section I.
 */
const base = (venueId: string) => `/admin/venue/${venueId}/menu-consumption-recipe`;

const menuConsumptionRecipeService = {
  /** List recipes for a venue. Defaults to active=true; pass active='all' or 'false' to widen. */
  getRecipes: async (
    venueId: string,
    page = 1,
    pageSize = 20,
    active?: 'true' | 'false' | 'all',
  ): Promise<PaginatedResponse<MenuConsumptionRecipe>> => {
    const params: Record<string, unknown> = { page, pageSize };
    if (active) params.active = active;
    const response = await api.get(base(venueId), { params });
    return response.data;
  },

  /** Get a single recipe. */
  getRecipe: async (venueId: string, recipeId: string): Promise<MenuConsumptionRecipe> => {
    const response = await api.get(`${base(venueId)}/${recipeId}`);
    return response.data;
  },

  /** Create a recipe (components array may be empty). */
  createRecipe: async (
    venueId: string,
    data: { name: string; components?: MenuConsumptionRecipeComponent[]; notes?: string },
  ): Promise<MenuConsumptionRecipe> => {
    const response = await api.post(base(venueId), data);
    return response.data;
  },

  /** Partial update. When `components` is provided, it fully replaces the array. */
  updateRecipe: async (
    venueId: string,
    recipeId: string,
    data: Partial<{
      name: string;
      components: MenuConsumptionRecipeComponent[];
      notes: string;
      active: boolean;
    }>,
  ): Promise<MenuConsumptionRecipe> => {
    const response = await api.put(`${base(venueId)}/${recipeId}`, data);
    return response.data;
  },

  /** Soft-delete (active=false). */
  deleteRecipe: async (venueId: string, recipeId: string): Promise<void> => {
    await api.delete(`${base(venueId)}/${recipeId}`);
  },
};

export default menuConsumptionRecipeService;
